import { useEffect, useMemo, useState } from 'react';
import { Line } from 'recharts';
import TimeSeriesLineChart from '@/components/charts/TimeSeriesLineChart';
import { formatNumber } from '@/utils';
import { formatBigNumbers } from '@/utils/numberFormattingUtils';
import type {
  EpisodeLaunchComparisonResponse,
  LaunchWindowDays,
  SelectedItemWithColor,
} from '@/types/content';
import { mixpanelTrack } from '@/utils/mixpanelUtils/asyncMixpanel';
import { MIXPANEL_EVENT, RevenueDashboardWidget } from '@/utils/mixpanelUtils/events';

interface PostLaunchComparisonChartProps {
  selectedItems: SelectedItemWithColor[];
  data: EpisodeLaunchComparisonResponse | undefined;
  windowDays: LaunchWindowDays;
}

interface ChartDataPoint {
  dayOffset: number;
  [episodeId: string]: number;
}

/**
 * Pivots flat API rows into a wide array keyed by dayOffset for Recharts.
 * Done client-side so the API returns a generic, well-typed row format
 * rather than a Recharts-specific shape with dynamic episode-ID keys.
 */
const buildChartData = (
  data: EpisodeLaunchComparisonResponse | undefined,
  windowDays: LaunchWindowDays
): ChartDataPoint[] => {
  const points = new Map<number, ChartDataPoint>();

  for (let offset = 0; offset <= windowDays; offset++) {
    points.set(offset, { dayOffset: offset });
  }

  if (data?.rows) {
    for (const row of data.rows) {
      const existing = points.get(row.dayOffset);
      if (existing) {
        existing[row.episodeId] = row.downloadsAndViews;
        existing[`${row.episodeId}__daily`] = row.dailyDownloadsAndViews;
      }
    }
  }

  return Array.from(points.values()).sort((pointA, pointB) => pointA.dayOffset - pointB.dayOffset);
};

const formatTooltipHeading = (dayOffset: number): string => {
  if (dayOffset === 0) return 'Publish day';
  return `Total after ${dayOffset} ${dayOffset === 1 ? 'day' : 'days'}`;
};

interface TooltipContentProps {
  payload: ChartDataPoint;
  selectedItems: SelectedItemWithColor[];
  windowDays: LaunchWindowDays;
}

const PostLaunchTooltipContent = ({ payload, selectedItems, windowDays }: TooltipContentProps) => {
  useEffect(() => {
    if (payload?.dayOffset == null) return;
    const timeout = setTimeout(() => {
      mixpanelTrack(MIXPANEL_EVENT.contentPerformance.chart.postLaunchTooltipHover, {
        widget: RevenueDashboardWidget.ContentPerformanceChart,
        page: 'content-performance',
        windowDays,
      });
    }, 1000);
    return () => clearTimeout(timeout);
  }, [payload?.dayOffset, windowDays]);

  if (payload?.dayOffset == null) return null;

  return (
    <div className="flex flex-col gap-1">
      <p className="text-sm font-medium mr-6">{formatTooltipHeading(payload.dayOffset)}</p>
      {selectedItems.map((item) => {
        const value = payload[item.id];
        if (value == null) return null;
        const dailyDelta = payload[`${item.id}__daily`] as number | undefined;
        return (
          <p className="text-xs inline-flex items-center gap-1" key={item.id}>
            <span
              className="w-3 h-3 rounded-full shrink-0"
              style={{ backgroundColor: item.color }}
            />
            <span className="truncate max-w-[140px]">{item.label}:</span>
            <strong>{formatNumber('en', value as number)}</strong>
            {dailyDelta != null && dailyDelta > 0 && (
              <span className="text-gray-400">(+{formatNumber('en', dailyDelta)})</span>
            )}
          </p>
        );
      })}
    </div>
  );
};

/**
 * Cumulative line chart comparing episode performance relative to their
 * publish date. X-axis is numeric day offsets (0 = publish day)
 * and Y-axis shows cumulative downloads + views.
 */
const PostLaunchComparisonChart = ({
  selectedItems,
  data,
  windowDays,
}: PostLaunchComparisonChartProps) => {
  const [animateOnMount, setAnimateOnMount] = useState(true);

  const chartData = useMemo(() => buildChartData(data, windowDays), [data, windowDays]);

  useEffect(() => {
    if (data?.rows && data.rows.length > 0 && animateOnMount) {
      setAnimateOnMount(false);
    }
  }, [data, animateOnMount]);

  const baseLineProps = {
    dot: false,
    connectNulls: true,
    isAnimationActive: animateOnMount,
    animationDuration: 500,
    animationBegin: 0,
    animationEasing: 'ease-out' as const,
    strokeWidth: 2,
    type: 'linear' as const,
    fillOpacity: 0,
  };

  return (
    <TimeSeriesLineChart<ChartDataPoint>
      data={chartData}
      dateKey="dayOffset"
      xAxisTickFormatter={String}
      yAxisFormatter={(value) => (value !== 0 ? formatBigNumbers(value) : '')}
      ariaLabel="Post-launch episode comparison"
      tooltipContent={(payload) => (
        <PostLaunchTooltipContent
          payload={payload}
          selectedItems={selectedItems}
          windowDays={windowDays}
        />
      )}
      height={300}
      xAxisProps={{
        type: 'number',
        domain: [0, windowDays],
        allowDecimals: false,
        tickCount: windowDays + 1,
        interval: 'preserveStartEnd',
      }}
    >
      {selectedItems.map((item) => (
        <Line
          {...baseLineProps}
          key={`line-${item.id}`}
          name={item.label}
          dataKey={item.id}
          stroke={item.color}
        />
      ))}
    </TimeSeriesLineChart>
  );
};

export default PostLaunchComparisonChart;
